import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, CalendarCheck, Check, Cloud, Eye, EyeOff, Loader2, MailCheck, X } from 'lucide-react';
import { BrandMark } from '../../components/ui/BrandMark';
import { t } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';
import type { EmailAuthResult, LegalLinksDTO } from '../../global';
import { checkPasswordRules, isPasswordStrong, PASSWORD_RULE_IDS, passwordRuleLabel } from '../preferences/passwordRules';

interface SignInViewProps {
  onSignIn: () => void;
  /** E-postayla giriş/kayıt oturumu açtı — oturum durumu yeniden okunur. */
  onSignedIn: () => void;
  isSigningIn: boolean;
  error: string | null;
}

/** Google'ın marka renkli "G" işareti. */
const GoogleMark = () => (
  <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 6.9-10 6.9-17.3z" />
    <path fill="#FBBC05" d="M10.4 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.8-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.8-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.3 0-11.7-3.7-13.6-9.0l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

/** Apple logosu (Apple ile Giriş düğmesi, yalnızca iOS uygulamasında). */
const AppleMark = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true" fill="currentColor">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

/**
 * E-postayla akışın adımları. Kayıt ve şifre sıfırlama adrese giden 6 haneli
 * kodla tamamlanıyor; kod girilmeden hesap kullanılamaz (bkz. webapp/src/emailAuth.ts).
 */
type Mode = 'signin' | 'signup' | 'verify' | 'forgot' | 'reset';

/** Sunucunun iki kod gönderimi arasında beklettiği süre (web/lib/auth/email-codes.ts). */
const RESEND_COOLDOWN_SEC = 45;

const inputClass =
  'w-full px-3.5 py-2.5 rounded-xl bg-[#1A1A1D] border border-[#3A3A3E] text-[14px] text-[#F0EEE6] placeholder:text-neutral-500 focus:outline-none focus:border-[#46CBE2] transition-colors select-text';

const primaryButtonClass =
  'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#1DA4BA] text-white font-semibold text-[14px] hover:bg-[#1793A8] disabled:opacity-60 disabled:cursor-not-allowed transition-colors active:scale-[0.99]';

const linkClass = 'text-[#46CBE2] hover:text-[#7ADCEC] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block text-left space-y-1.5">
    <span className="text-[12px] font-semibold text-neutral-300">{label}</span>
    {children}
  </label>
);

const PasswordInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  placeholder?: string;
}> = ({ value, onChange, autoComplete, placeholder }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        maxLength={72}
        required
        className={`${inputClass} pr-10`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-neutral-300"
        aria-label={visible ? t('Şifreyi gizle', 'Hide password') : t('Şifreyi göster', 'Show password')}
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const PasswordRules: React.FC<{ password: string }> = ({ password }) => {
  const rules = checkPasswordRules(password);
  return (
    <ul className="grid grid-cols-2 gap-x-3 gap-y-1 pt-0.5">
      {PASSWORD_RULE_IDS.map((rule) => (
        <li key={rule} className={`flex items-center gap-1.5 text-[11.5px] ${rules[rule] ? 'text-emerald-400' : 'text-neutral-500'}`}>
          {rules[rule] ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          {passwordRuleLabel(rule)}
        </li>
      ))}
    </ul>
  );
};

/**
 * Giriş ekranı. Oturum açılmadan uygulamanın hiçbir bölümü görünmüyor —
 * toplantılar artık Supabase'de saklandığı için kullanıcı kimliği olmadan
 * okunacak/yazılacak veri de yok.
 *
 * Web'de Google'ın yanında e-posta + şifreyle giriş ve kayıt da var
 * (`window.zabit.emailAuth`); masaüstü köprüsünde yok, orada yalnızca Google.
 * `/kayit` adresi doğrudan kayıt formunu açar (tanıtım sitesi oraya bağlanıyor).
 */
export const SignInView: React.FC<SignInViewProps> = ({ onSignIn, onSignedIn, isSigningIn, error }) => {
  const [legalLinks, setLegalLinks] = useState<LegalLinksDTO | null>(null);
  useEffect(() => {
    window.zabit.getLegalLinks().then(setLegalLinks).catch(() => {});
  }, []);
  const open = (url: string | null) => url && void window.zabit.openExternal(url).catch(() => {});

  const emailAuth = window.zabit.emailAuth;
  const [mode, setMode] = useState<Mode>(() =>
    IS_WEB && /^\/kayit\/?$/.test(window.location.pathname) ? 'signup' : 'signin'
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (mode === 'verify' || mode === 'reset') codeRef.current?.focus();
  }, [mode]);

  const go = (next: Mode) => {
    setMode(next);
    setFormError(null);
    setNotice(null);
    setCode('');
    if (next === 'signup' || next === 'reset') setPassword('');
  };

  /** Bir adımı çalıştırır; başarılıysa `onOk`, değilse hatayı gösterir. */
  const run = async (step: () => Promise<EmailAuthResult>, onOk: () => void, onFail?: (result: Extract<EmailAuthResult, { ok: false }>) => boolean) => {
    if (busy) return;
    setBusy(true);
    setFormError(null);
    try {
      const result = await step();
      if (result.ok) onOk();
      else if (!onFail?.(result)) {
        setFormError(result.error);
        if (result.retryAfterSec) setCooldown(result.retryAfterSec);
      }
    } catch (err) {
      setFormError((err as Error).message || t('Bir sorun oluştu. Tekrar dene.', 'Something went wrong. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const codeSentNotice = () =>
    t(`${email.trim()} adresine 6 haneli bir kod gönderdik.`, `We sent a 6-digit code to ${email.trim()}.`);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!emailAuth) return;
    switch (mode) {
      case 'signin':
        return void run(
          () => emailAuth.signIn(email, password),
          onSignedIn,
          (result) => {
            if (result.reason !== 'email_not_confirmed') return false;
            go('verify');
            setNotice(`${result.error} ${codeSentNotice()}`);
            setCooldown(result.retryAfterSec ?? RESEND_COOLDOWN_SEC);
            return true;
          }
        );
      case 'signup':
        if (!isPasswordStrong(password)) {
          setFormError(t('Şifre aşağıdaki kurallara uymalı.', 'The password must meet the rules below.'));
          return;
        }
        return void run(
          () => emailAuth.signUp(name, email, password),
          () => {
            go('verify');
            setNotice(codeSentNotice());
            setCooldown(RESEND_COOLDOWN_SEC);
          }
        );
      case 'verify':
        return void run(() => emailAuth.verifySignup(email, code), onSignedIn, (result) => {
          if (result.reason !== 'wrong') setCode('');
          return false;
        });
      case 'forgot':
        return void run(
          () => emailAuth.requestPasswordReset(email),
          () => {
            go('reset');
            setNotice(codeSentNotice());
            setCooldown(RESEND_COOLDOWN_SEC);
          }
        );
      case 'reset':
        if (!isPasswordStrong(password)) {
          setFormError(t('Yeni şifre aşağıdaki kurallara uymalı.', 'The new password must meet the rules below.'));
          return;
        }
        return void run(() => emailAuth.resetPassword(email, code, password), onSignedIn, (result) => {
          if (result.reason !== 'wrong' && result.reason !== 'weak_password') setCode('');
          return false;
        });
    }
  };

  const resend = () => {
    if (!emailAuth || cooldown > 0) return;
    void run(
      () => (mode === 'reset' ? emailAuth.requestPasswordReset(email) : emailAuth.resendSignupCode(email)),
      () => {
        setCode('');
        setNotice(t('Yeni kod gönderildi. Önceki kod artık geçersiz.', 'A new code has been sent. The previous code no longer works.'));
        setCooldown(RESEND_COOLDOWN_SEC);
        codeRef.current?.focus();
      }
    );
  };

  const onCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    // Kayıtta kod tamamlanınca beklemeden doğrula (sıfırlamada yeni şifre de gerekiyor).
    if (digits.length === 6 && mode === 'verify' && emailAuth && !busy) {
      void run(() => emailAuth.verifySignup(email, digits), onSignedIn, (result) => {
        if (result.reason !== 'wrong') setCode('');
        return false;
      });
    }
  };

  const shownError = formError ?? (mode === 'signin' || mode === 'signup' ? error : null);
  const codeStep = mode === 'verify' || mode === 'reset';

  const googleButton = (
    <button
      type="button"
      onClick={onSignIn}
      disabled={isSigningIn || busy}
      className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl bg-[#F0EEE6] text-neutral-900 font-semibold text-[14px] hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm active:scale-[0.99]"
    >
      {isSigningIn ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>
            {IS_WEB
              ? t('Google’a yönlendiriliyorsunuz…', 'Redirecting to Google…')
              : t('Tarayıcınızı kontrol edin...', 'Check your browser…')}
          </span>
        </>
      ) : (
        <>
          <GoogleMark />
          <span>
            {mode === 'signup' ? t('Google ile kayıt ol', 'Sign up with Google') : t('Google ile Giriş Yap', 'Sign in with Google')}
          </span>
        </>
      )}
    </button>
  );

  // iOS: Apple ile Giriş (App Store kuralı 4.8). Köprüde yoksa düğme de yok.
  const appleSignIn = window.zabit.appleSignIn;
  const [appleBusy, setAppleBusy] = useState(false);
  const signInWithApple = async () => {
    if (!appleSignIn || appleBusy) return;
    setAppleBusy(true);
    setFormError(null);
    try {
      const result = await appleSignIn();
      if (result.ok) onSignedIn();
      else if (!result.cancelled && result.error) setFormError(result.error);
    } finally {
      setAppleBusy(false);
    }
  };
  const appleButton = appleSignIn ? (
    <button
      type="button"
      onClick={() => void signInWithApple()}
      disabled={appleBusy || busy || isSigningIn}
      className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl bg-white text-black font-semibold text-[14px] hover:bg-neutral-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm active:scale-[0.99]"
    >
      {appleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AppleMark />}
      <span>{mode === 'signup' ? t('Apple ile kayıt ol', 'Sign up with Apple') : t('Apple ile Giriş Yap', 'Sign in with Apple')}</span>
    </button>
  ) : null;

  const heading: Record<Mode, string> = {
    signin: t('Giriş yap', 'Sign in'),
    signup: t('Hesap oluştur', 'Create an account'),
    verify: t('E-postanı doğrula', 'Verify your email'),
    forgot: t('Şifreni sıfırla', 'Reset your password'),
    reset: t('Yeni şifre belirle', 'Set a new password'),
  };

  const submitLabel: Record<Mode, string> = {
    signin: t('Giriş yap', 'Sign in'),
    signup: t('Kayıt ol', 'Sign up'),
    verify: t('Doğrula ve devam et', 'Verify and continue'),
    forgot: t('Kod gönder', 'Send code'),
    reset: t('Şifreyi değiştir ve giriş yap', 'Change password and sign in'),
  };

  return (
    <div className="h-full w-full overflow-y-auto bg-[#111113] text-[#F0EEE6] select-none px-6">
      <div className="min-h-full flex items-center justify-center py-10">
        <div className="w-full max-w-sm text-center space-y-7">
          <div className="space-y-2.5">
            <BrandMark className="w-14 h-14 mx-auto" />
            <h1 className="text-sm font-bold tracking-[0.18em]">TALKNOTE</h1>
            <p className="text-[13px] text-neutral-400 leading-relaxed">
              {t(
                'Toplantılarınızı kaydedin, otomatik transkript ve özet alın.',
                'Record your meetings and get automatic transcripts and summaries.'
              )}
            </p>
          </div>

          {!emailAuth ? (
            googleButton
          ) : (
            <div className="space-y-4">
              {codeStep || mode === 'forgot' ? (
                <div className="flex items-center gap-2 text-left">
                  <button
                    type="button"
                    onClick={() => go(mode === 'verify' ? 'signup' : mode === 'reset' ? 'forgot' : 'signin')}
                    className="p-1 -ml-1 rounded-lg text-neutral-400 hover:text-neutral-200 hover:bg-white/5"
                    aria-label={t('Geri', 'Back')}
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <h2 className="text-[15px] font-semibold">{heading[mode]}</h2>
                </div>
              ) : (
                <>
                  {googleButton}
                  {appleButton}
                  <div className="flex items-center gap-3 text-[11.5px] text-neutral-500">
                    <div className="h-px flex-1 bg-[#3A3A3E]" />
                    {t('ya da e-postayla', 'or with email')}
                    <div className="h-px flex-1 bg-[#3A3A3E]" />
                  </div>
                </>
              )}

              {notice && (
                <div className="text-left text-[12.5px] text-neutral-300 flex items-start gap-2 bg-[#1DA4BA]/10 border border-[#1DA4BA]/30 rounded-xl p-3">
                  <MailCheck className="w-4 h-4 shrink-0 mt-0.5 text-[#46CBE2]" />
                  <span className="leading-relaxed">{notice}</span>
                </div>
              )}

              <form onSubmit={submit} className="space-y-3.5" noValidate={false}>
                {mode === 'signup' && (
                  <Field label={t('Ad soyad', 'Full name')}>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoComplete="name"
                      maxLength={80}
                      required
                      className={inputClass}
                    />
                  </Field>
                )}

                {(mode === 'signin' || mode === 'signup' || mode === 'forgot') && (
                  <Field label={t('E-posta', 'Email')}>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      maxLength={320}
                      required
                      placeholder="ornek@firma.com"
                      className={inputClass}
                    />
                  </Field>
                )}

                {codeStep && (
                  <Field label={t('Doğrulama kodu', 'Verification code')}>
                    <input
                      ref={codeRef}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="\d{6}"
                      value={code}
                      onChange={(e) => onCodeChange(e.target.value)}
                      maxLength={6}
                      required
                      placeholder="••••••"
                      // Harf aralığı son rakamın sağına da ekleniyor; ortalama kaymasın.
                      style={{ textIndent: '0.5em' }}
                      className={`${inputClass} text-center text-[22px] font-semibold tracking-[0.5em] tabular-nums`}
                    />
                  </Field>
                )}

                {(mode === 'signin' || mode === 'signup' || mode === 'reset') && (
                  <Field label={mode === 'reset' ? t('Yeni şifre', 'New password') : t('Şifre', 'Password')}>
                    <PasswordInput
                      value={password}
                      onChange={setPassword}
                      autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                    />
                    {mode !== 'signin' && <PasswordRules password={password} />}
                  </Field>
                )}

                {mode === 'signin' && (
                  <div className="text-right -mt-1">
                    <button type="button" onClick={() => go('forgot')} className={`text-[12px] ${linkClass}`}>
                      {t('Şifremi unuttum', 'Forgot password?')}
                    </button>
                  </div>
                )}

                {shownError && (
                  <div className="text-left text-[12px] text-rose-400 flex items-start gap-2 bg-rose-500/10 border border-rose-500/25 rounded-xl p-3">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{shownError}</span>
                  </div>
                )}

                <button type="submit" disabled={busy || isSigningIn} className={primaryButtonClass}>
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitLabel[mode]}
                </button>
              </form>

              {codeStep && (
                <p className="text-[12px] text-neutral-400">
                  {t('Kod gelmedi mi? Spam klasörüne bak ya da ', 'Didn’t get it? Check your spam folder or ')}
                  <button type="button" onClick={resend} disabled={busy || cooldown > 0} className={linkClass}>
                    {cooldown > 0
                      ? t(`${cooldown} sn sonra yeniden gönder`, `resend in ${cooldown}s`)
                      : t('yeniden gönder', 'resend it')}
                  </button>
                  .
                </p>
              )}

              {mode === 'signin' && (
                <p className="text-[12.5px] text-neutral-400">
                  {t('Hesabın yok mu? ', 'No account yet? ')}
                  <button type="button" onClick={() => go('signup')} className={linkClass}>
                    {t('Kayıt ol', 'Sign up')}
                  </button>
                </p>
              )}
              {mode === 'signup' && (
                <p className="text-[12.5px] text-neutral-400">
                  {t('Zaten hesabın var mı? ', 'Already have an account? ')}
                  <button type="button" onClick={() => go('signin')} className={linkClass}>
                    {t('Giriş yap', 'Sign in')}
                  </button>
                </p>
              )}
            </div>
          )}

          {!emailAuth && error && (
            <div className="text-left text-[12px] text-rose-400 flex items-start gap-2 bg-rose-500/10 border border-rose-500/25 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="leading-relaxed">{error}</span>
            </div>
          )}

          {/* İzinlerin ne işe yaradığı açıkça yazılı — kullanıcı takvim
              erişimini neden onayladığını bilsin. */}
          {!codeStep && mode !== 'forgot' && (
            <div className="text-left space-y-2.5 text-[12px] text-neutral-400 border-t border-[#3A3A3E] pt-5">
              <div className="flex items-start gap-2.5">
                <CalendarCheck className="w-4 h-4 text-[#46CBE2] shrink-0 mt-0.5" />
                {emailAuth ? (
                  <span>
                    {t(
                      'Google ile girersen takvimin de bağlanır (salt-okunur): yaklaşan toplantıların görünür, bot tek tıkla katılır. E-postayla girersen Google ya da Outlook takvimini sonra Bağlantılar’dan bağlayabilirsin.',
                      'Sign in with Google and your calendar is connected too (read-only): your upcoming meetings show up and the bot joins in one click. With email, you can connect your Google or Outlook calendar later under Connections.'
                    )}
                  </span>
                ) : (
                  <span>
                    <strong className="text-neutral-300 font-semibold">
                      {t('Takvim erişimi gereklidir.', 'Calendar access is required.')}
                    </strong>{' '}
                    {t(
                      'Yaklaşan toplantılarınızı göstermek ve tek tıkla katılmanızı sağlamak için takviminizi salt-okunur olarak okuruz.',
                      'We read your calendar (read-only) to show your upcoming meetings and let you join them in one click.'
                    )}
                  </span>
                )}
              </div>
              {/* Kayıtlar S3'e yükleniyor — "bu bilgisayarda kalır" demek yanlış olurdu. */}
              <div className="flex items-start gap-2.5">
                <Cloud className="w-4 h-4 text-[#46CBE2] shrink-0 mt-0.5" />
                <span>
                  {t(
                    'Ses kayıtlarınız, transkriptleriniz ve notlarınız hesabınıza bağlı olarak bulutta saklanır; özetler yapay zekâ servisleriyle çıkarılır.',
                    'Your recordings, transcripts and notes are stored in the cloud under your account; summaries are generated with AI services.'
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Onay girişten sonraki ekranda açıkça alınıyor; burada yalnızca okunabilsin. */}
          {(legalLinks?.terms || legalLinks?.privacy) && (
            <div className="flex justify-center gap-4 text-[11.5px] text-neutral-500">
              {legalLinks.terms && (
                <button onClick={() => open(legalLinks.terms)} className="hover:text-neutral-300 transition-colors">
                  {t('Kullanım Koşulları', 'Terms of Use')}
                </button>
              )}
              {legalLinks.privacy && (
                <button onClick={() => open(legalLinks.privacy)} className="hover:text-neutral-300 transition-colors">
                  {t('Gizlilik ve KVKK', 'Privacy')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
